/**
 * Remember Me storage helper (FINDING-010).
 * Persists email only — never passwords — in localStorage when the user opts in.
 */

import { safeGetJSON, safeSetJSON, safeRemoveItem } from "./storage";

const REMEMBER_ME_KEY = "login_remember_data";

/**
 * Strip any legacy password field from a raw remember-me payload.
 * @param {unknown} raw
 * @returns {{ email: string, remember: true } | null}
 */
function normalizeRememberMe(raw) {
  if (!raw || typeof raw !== "object" || raw.remember !== true) {
    return null;
  }
  const email = typeof raw.email === "string" ? raw.email.trim() : "";
  if (!email) return null;
  return { email, remember: true };
}

/**
 * Get stored remember-me data if present and valid (remember === true, email string).
 * Migrates legacy entries that included a password by rewriting email-only.
 * @returns {{ email: string, remember: true } | null}
 */
export const getRememberMeData = () => {
  const raw = safeGetJSON(REMEMBER_ME_KEY, null);
  const normalized = normalizeRememberMe(raw);
  if (!normalized) {
    if (raw != null) {
      // Corrupt or remember=false — drop the key.
      safeRemoveItem(REMEMBER_ME_KEY);
    }
    return null;
  }

  // FINDING-010: purge plaintext password if an older build left it behind.
  if (
    raw &&
    typeof raw === "object" &&
    Object.prototype.hasOwnProperty.call(raw, "password")
  ) {
    safeSetJSON(REMEMBER_ME_KEY, {
      email: normalized.email,
      remember: true,
    });
  }

  return normalized;
};

/**
 * Save remember-me email preference (no password).
 * @param {{ email: string, remember: boolean }} data
 * @returns {boolean}
 */
export const setRememberMeData = ({ email, remember }) => {
  if (!remember) {
    return clearRememberMeData();
  }
  return safeSetJSON(REMEMBER_ME_KEY, {
    email: String(email ?? "").trim(),
    remember: true,
  });
};

/**
 * Remove stored remember-me data.
 * @returns {boolean}
 */
export const clearRememberMeData = () => {
  return safeRemoveItem(REMEMBER_ME_KEY);
};

// One-shot migration on module load for existing browsers.
getRememberMeData();
